import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Capitalize the first character of a string, leaving the rest untouched.
 * Returns an empty string unchanged.
 */
export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Validate an image file against a size limit and an optional allow-list of
 * MIME types. When `acceptedTypes` is omitted, any `image/*` type is allowed.
 */
export function isValidImageFile(
  file: File | null | undefined,
  opts: { maxBytes: number; acceptedTypes?: string[] }
): boolean {
  if (!file) return false
  const typeAllowed = opts.acceptedTypes
    ? opts.acceptedTypes.includes(file.type)
    : file.type.startsWith('image/')
  return typeAllowed && file.size <= opts.maxBytes
}

/**
 * Format a byte count as a human-readable size string.
 *
 * @param bytes - The size in bytes
 * @param opts.decimals - Fixed number of fraction digits (default 0)
 * @param opts.sizeType - 'normal' (KB, MB) or 'accurate' (KiB, MiB)
 * @param opts.trimTrailingZeros - Strip trailing zero fraction digits, e.g.
 *   "1.50 KB" -> "1.5 KB" and "1.00 KB" -> "1 KB"
 */
export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number
    sizeType?: 'accurate' | 'normal'
    trimTrailingZeros?: boolean
  } = {}
): string {
  const { decimals = 0, sizeType = 'normal', trimTrailingZeros = false } = opts

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB']

  if (bytes === 0) return '0 Byte'

  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const value = (bytes / Math.pow(1024, i)).toFixed(decimals)
  const formatted = trimTrailingZeros ? String(parseFloat(value)) : value

  return `${formatted} ${
    sizeType === 'accurate'
      ? (accurateSizes[i] ?? 'Bytest')
      : (sizes[i] ?? 'Bytes')
  }`
}

/**
 * Format currency amount
 * @param amount - Amount in cents (or dollars if currency is not USD)
 * @param currency - Currency code (default: 'usd')
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'usd'): string {
  const amountInDollars = currency.toLowerCase() === 'usd' ? amount / 100 : amount
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInDollars)
}

/**
 * Format currency amount from dollars (not cents)
 * @param amount - Amount in dollars
 * @param currency - Currency code (default: 'usd')
 * @returns Formatted currency string
 */
export function formatCurrencyFromDollars(amount: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Get the name/title from either CompanyEntity or OrganisationWithBilling
 * CompanyEntity has 'name', OrganisationWithBilling has 'title'
 */
export function getOrganisationName(
  org: { name?: string; title?: string } | null | undefined
): string {
  if (!org) return ''
  return 'name' in org && org.name ? org.name : org.title || ''
}
